/**
 * Session Storage Manager for Filter Values
 * Handles saving and loading filter values to/from session storage
 */

import { CONFIG } from '../../core/config.js';
import {
    BaseSessionStorageManager,
    clearByPrefix,
    getCurrentPageIdentifier
} from '../../core/session-storage-utils.js';

/**
 * Filter Session Storage Manager class
 * Extends BaseSessionStorageManager for filter-specific functionality
 */
export class FilterSessionStorageManager extends BaseSessionStorageManager {
    constructor() {
        super(CONFIG.SESSION_STORAGE.FILTER_PREFIX);
    }

    /**
     * Save filter values to session storage
     * @param {string} pageIdentifier - Unique identifier for the page
     * @param {Object} filterValues - Filter values to save
     * @returns {boolean} Success status
     */
    saveFilterValues(pageIdentifier, filterValues) {
        return this.save(pageIdentifier, filterValues);
    }

    /**
     * Load filter values from session storage
     * @param {string} pageIdentifier - Unique identifier for the page
     * @returns {Object|null} Filter values or null if not found/expired
     */
    loadFilterValues(pageIdentifier) {
        return this.load(pageIdentifier);
    }

    /**
     * Remove filter values from session storage
     * @param {string} pageIdentifier - Unique identifier for the page
     * @returns {boolean} Success status
     */
    removeFilterValues(pageIdentifier) {
        return this.remove(pageIdentifier);
    }
}

/**
 * Default instance of FilterSessionStorageManager
 */
export const sessionStorageManager = new FilterSessionStorageManager();

/**
 * Utility functions for easy access
 */
export const FilterStorage = {
    /**
     * Save current filter values for the current page
     * @param {Object} filterValues - Filter values to save
     * @returns {boolean} Success status
     */
    save(filterValues) {
        const pageIdentifier = getCurrentPageIdentifier();
        return sessionStorageManager.saveFilterValues(pageIdentifier, filterValues);
    },

    /**
     * Load filter values for the current page
     * @returns {Object|null} Filter values or null if not found/expired
     */
    load() {
        const pageIdentifier = getCurrentPageIdentifier();
        return sessionStorageManager.loadFilterValues(pageIdentifier);
    },

    /**
     * Remove filter values for the current page
     * @returns {boolean} Success status
     */
    remove() {
        const pageIdentifier = getCurrentPageIdentifier();
        return sessionStorageManager.removeFilterValues(pageIdentifier);
    },

    /**
     * Check if session storage is available
     * @returns {boolean} True if available
     */
    isAvailable() {
        return sessionStorageManager.isAvailable();
    },

    /**
     * Clear all saved filter values across all pages
     * @returns {boolean} Success status
     */
    clearAll() {
        return clearByPrefix(CONFIG.SESSION_STORAGE.FILTER_PREFIX);
    }
};

export default FilterStorage;
